import {
  AssetType,
  PageRevisionSchemaProperties,
  TtSchemaFieldEntityDto,
} from '../../../types/graphql';
import { generateFieldProperties, mapProperties } from '../../../utils/helper';
import { TTSchemaWithSubschemas } from '../dynamic-data/field-list';

export interface IDummyFieldValueFactory {
  createField: (field: any) => PageRevisionSchemaProperties;
  Image: (field: any) => PageRevisionSchemaProperties;
  Instagram: (field: any) => PageRevisionSchemaProperties;
  Link: (field: any) => PageRevisionSchemaProperties;
  TextArea: (field: any) => PageRevisionSchemaProperties;
  Text: (field: any) => PageRevisionSchemaProperties;
  TextSelect: (field: any) => PageRevisionSchemaProperties;
  Subschema: (field: any) => PageRevisionSchemaProperties;
}

export type ValueGeneratorFieldType = Pick<
  TtSchemaFieldEntityDto,
  'id' | 'name' | 'hasMultipleValues' | 'typeDefinition'
>;

export const createDummyImageValue = () => ({
  fileName: '',
  altText: '',
  sizeInBytes: 0,
  url: '',
  width: 0,
  height: 0,
  type: AssetType.Image,
  mimeType: '',
});

export type LinkField = {
  target: string;
  url: string;
  rel: string;
  type: 'page' | 'external-url' | 'anchor';
  pageName?: string;
};

/**
 * Generates dummy link field type for new fields
 *
 * @returns
 */
export const createDummyLinkFieldValue = (): LinkField => ({
  url: '',
  target: '_self',
  rel: '',
  type: 'external-url',
  pageName: '',
});

class DummyFieldValueFactory implements IDummyFieldValueFactory {
  createField(field: ValueGeneratorFieldType, singleValueDefaultValue: any = '') {
    return {
      fieldId: field.id,
      name: field.name,
      value: this.getFieldDefaultValue(field, singleValueDefaultValue),
      isDynamic: false,
    };
  }

  getFieldDefaultValue(field: ValueGeneratorFieldType, singleValueFieldDefaultValue: any = '') {
    let defaultValue = [];
    if (!field.hasMultipleValues) {
      defaultValue = [singleValueFieldDefaultValue];
    }
    if (field.typeDefinition?.defaultValue && Array.isArray(field.typeDefinition?.defaultValue)) {
      defaultValue = field.typeDefinition.defaultValue;
    } else if (field.typeDefinition?.defaultValue) {
      defaultValue = [field.typeDefinition.defaultValue];
    }
    return defaultValue;
  }

  Link(field: ValueGeneratorFieldType): PageRevisionSchemaProperties {
    return this.createField(field, createDummyLinkFieldValue());
  }

  Image(field: ValueGeneratorFieldType): PageRevisionSchemaProperties {
    return this.createField(field, createDummyImageValue());
  }

  GmbLocation(field: ValueGeneratorFieldType): PageRevisionSchemaProperties {
    return this.createField(field, {});
  }

  Instagram(field: ValueGeneratorFieldType): PageRevisionSchemaProperties {
    return this.createField(field, { accountId: '' });
  }

  Page(field: ValueGeneratorFieldType): PageRevisionSchemaProperties {
    return this.createField(field, null);
  }

  Text(field: ValueGeneratorFieldType): PageRevisionSchemaProperties {
    return this.createField(field, '');
  }

  RichText(
    field: Pick<TtSchemaFieldEntityDto, 'id' | 'name' | 'hasMultipleValues'>,
  ): PageRevisionSchemaProperties {
    return this.Text(field);
  }

  TextArea(
    field: Pick<TtSchemaFieldEntityDto, 'id' | 'name' | 'hasMultipleValues'>,
  ): PageRevisionSchemaProperties {
    return this.Text(field);
  }

  TextSelect(
    field: Pick<TtSchemaFieldEntityDto, 'id' | 'name' | 'hasMultipleValues'>,
  ): PageRevisionSchemaProperties {
    return this.Text(field);
  }

  Datetime(
    field: Pick<TtSchemaFieldEntityDto, 'id' | 'name' | 'hasMultipleValues'>,
  ): PageRevisionSchemaProperties {
    return this.createField(field);
  }

  Form(
    field: Pick<TtSchemaFieldEntityDto, 'id' | 'name' | 'hasMultipleValues'>,
  ): PageRevisionSchemaProperties {
    return this.createField(field, null);
  }

  Subschema(
    field: ValueGeneratorFieldType,
    ttschema?: Partial<Pick<TTSchemaWithSubschemas, 'fields' | 'subSchemas'>> | null,
    subschemas?: Partial<Pick<TTSchemaWithSubschemas, 'id' | 'fields' | 'subSchemas'>>[] | null,
  ) {
    return {
      ...this.createField(field),
      value: [mapProperties(generateFieldProperties(ttschema, subschemas))],
    };
  }
}

export default new DummyFieldValueFactory();
